import { StatusBar } from '@capacitor/status-bar'
import { Capacitor } from '@capacitor/core'
import vuetify from '@/plugins/vuetify'

function themeState(isDark) {
    const theme = vuetify.framework.theme
    return {
        style: isDark ? 'DARK' : 'LIGHT',
        color: theme.themes[isDark ? 'dark' : 'light'].bg,
    }
}

class StatusBarManager {
    constructor() {
        this.stack = []
        this.base = null
        this.applied = null
        this.isNative = Capacitor.getPlatform() !== 'web'
        this.isAndroid = Capacitor.getPlatform() === 'android'
    }

    _current() {
        return this.stack[this.stack.length - 1] || this.base || themeState(vuetify.framework.theme.dark)
    }

    async _apply(state, force = false) {
        if (!this.isNative || !state) return
        if (!force && this.applied && this.applied.style === state.style && this.applied.color === state.color) return
        this.applied = { ...state }
        if (this.isAndroid) await StatusBar.setBackgroundColor({ color: state.color }).catch(() => {})
        await StatusBar.setStyle({ style: state.style })
        requestAnimationFrame(() => StatusBar.setStyle({ style: state.style }))
    }

    async setBase(options = {}) {
        const fallback = themeState(vuetify.framework.theme.dark)
        this.base = { style: options.style || fallback.style, color: options.color || fallback.color }
        if (this.stack.length === 0) await this._apply(this.base)
    }

    async set(isDark = false) {
        const state = themeState(isDark)
        this.stack.push(state)
        await this._apply(state)
    }

    async reset() {
        this.stack.pop()
        await this._apply(this._current())
    }

    async refresh() {
        await this._apply(this._current(), true)
    }

    async settle(attempts = 12, delay = 150) {
        if (!this.isNative) return
        for (let i = 0; i < attempts; i++) {
            await new Promise(resolve => setTimeout(resolve, delay))
            const info = await StatusBar.getInfo().catch(() => null)
            if (!info) return
            const state = this._current()
            if (info.style !== state.style) await this._apply(state, true)
        }
    }

    hide () {
        if (!this.isNative) return
        StatusBar.hide()
    }

    show () {
        if (!this.isNative) return
        StatusBar.show()
    }
}

export default new StatusBarManager()
